import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import useSsoSilentAttempt from './hooks/useSsoSilentAttempt';
import Login from './screens/Login';
import Home from './screens/Home';
import Integrations from './screens/Integrations';
import Products from './screens/Products';
import JobCards from './screens/JobCards';
import VanStock from './screens/VanStock';
import JobCardStock from './screens/JobCardStock';
import StockPulse from './screens/StockPulse';
import StockDisputes from './screens/StockDisputes';
import VanTransfers from './screens/VanTransfers';
import IspStockCounts from './screens/IspStockCounts';
import JobCardStockOrder from './screens/JobCardStockOrder';
import StockProcurement from './screens/StockProcurement';
import ProcurementApprovals from './screens/ProcurementApprovals';
import Receiving from './screens/Receiving';
import BackOrders from './screens/BackOrders';
import ItSupport from './screens/ItSupport';
import Inventory from './screens/Inventory';
import Directory from './screens/Directory';
import SimManager from './screens/SimManager';
import DeviceRegister from './screens/DeviceRegister';
import DevicesToCollect from './screens/DevicesToCollect';
import CommsDispatch from './screens/CommsDispatch';
import MessageAnalytics from './screens/MessageAnalytics';
import RecipientActivity from './screens/RecipientActivity';
import SupportConsole from './screens/SupportConsole';
import AssistantReview from './screens/AssistantReview';
import CommsInbox from './screens/CommsInbox';
import AbsenceReportingDashboard from './screens/AbsenceReportingDashboard';
import AbsenceReportingCategory from './screens/AbsenceReportingCategory';
import QuestAdmin from './screens/quest/QuestAdmin';
import QuestEditor from './screens/quest/QuestEditor';
import QuestPerson from './screens/quest/QuestPerson';
import PoleSurvey from './screens/PoleSurvey';
import PoleMap from './screens/PoleMap';
import PoleSurveyLog from './screens/PoleSurveyLog';
import ClimbCheck from './screens/ClimbCheck';
import EmailSignatures from './screens/EmailSignatures';
import AuthenticatedLayout from './components/AuthenticatedLayout';

function App() {
  const ssoAttempted = useSsoSilentAttempt();

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <Home />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        {/* Company directory — any signed-in user. Field-level visibility is
            enforced server-side, so this route needs no gate of its own. */}
        <Route
          path="/directory"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <Directory />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />

        {/* SIM manager — Logistics own the SIMs; the server gates it. */}
        <Route
          path="/sims"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <SimManager />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />

        {/* Device register — Logistics own the estate; the server gates it. */}
        <Route
          path="/devices"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <DeviceRegister />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />

        {/* Devices to Collect — Logistics collection queue; server-gated. */}
        <Route
          path="/devices/to-collect"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <DevicesToCollect />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />

        {/* Platform — full-admin only, and deliberately NOT under a department.
            The server enforces that on every request; this route existing just
            means a non-admin sees the screen's own 403 message rather than a
            blank page. */}
        <Route
          path="/procurement"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <StockProcurement />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/procurement/approvals"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <ProcurementApprovals />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/stock-pulse"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <StockPulse />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/isp-stock-counts"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <IspStockCounts />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/stock-disputes"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <StockDisputes />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/van-transfers"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <VanTransfers />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/job-card-stock"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <JobCardStock />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/job-card-stock-order"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <JobCardStockOrder />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/van-stock"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <VanStock />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/job-cards"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <JobCards />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/platform/products"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <Products />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/platform/integrations"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <Integrations />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        {/* Comms Dispatch — office/ops send + metrics; server gates to Operations/full-admin. */}
        <Route
          path="/comms"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <CommsDispatch />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />

        {/* Nano Quest admin — Communications; the tech API gates it on
            NEO2-Communications-Access. Employees take Quests in the FSM app. */}
        <Route
          path="/quest"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <QuestAdmin />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/quest/quests/:id"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <QuestEditor />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/quest/people/:oid"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <QuestPerson />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />

        {/* Message Analytics — Communications; server-gated like Comms Dispatch.
            The list is /comms/analytics; a single message's dashboard is the
            full-page /comms/analytics/:id (same component, reads the param). */}
        <Route
          path="/comms/analytics"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <MessageAnalytics />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/comms/analytics/:id"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <MessageAnalytics />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />

        {/* Recipient Activity — per-user engagement; server-gated like the other
            Communications screens. List = /comms/recipients; one person's history
            = /comms/recipients/:entraId (same component reads the param). */}
        <Route
          path="/comms/recipients"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <RecipientActivity />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/comms/recipients/:entraId"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <RecipientActivity />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />

        {/* Support Console — responder side of live assistance; server-gated on
            Nano-Support-Access (full admins bypass), like the other dept screens. */}
        <Route
          path="/support"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <SupportConsole />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />

        {/* AI Assistant Review — usage, unanswered/flagged answers and answers to
            re-check after a procedure changes. The dashboard server gates it on
            Nano-Support-Access (routes/assistantReview.js). */}
        <Route
          path="/support/assistant"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <AssistantReview />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />

        {/* My Messages — inbox for any signed-in user. */}
        <Route
          path="/messages"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <CommsInbox />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />

        {/* Deep link to a specific message (the email "Open message" button
            hits /go/message/:id which redirects here). Same inbox, auto-opens
            the message. Auth survives the MSAL redirect because loginRedirect
            stores this URL (navigateToLoginRequestUrl defaults true). */}
        <Route
          path="/messages/:id"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <CommsInbox />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        {/* Absence Reporting & Return to Work — HR own it; the server proxy
            (routes/absenceReporting.js) reads from FSM, which gates itself.
            /absence-reporting is the category tiles; picking one goes to its
            own full page, /absence-reporting/:category, for the person list
            and confirm-and-act flow (same list/detail split as
            /comms/analytics and /comms/analytics/:id). */}
        <Route
          path="/absence-reporting"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <AbsenceReportingDashboard />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/absence-reporting/:category"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <AbsenceReportingCategory />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/helpdesk"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <ItSupport />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/procurement/back-orders"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <BackOrders />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/procurement/receiving"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <Receiving />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/procurement/inventory"
          element={
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <Inventory />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          }
        />
        <Route
          path="/pole-map"
          element={(
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <PoleMap />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          )}
        />

        {/* Pole survey audit log (TKT-00042) — the API limits who can read it. */}
        <Route
          path="/pole-survey/log"
          element={(
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <PoleSurveyLog />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          )}
        />
        {/* Climb Check: tomorrow's ODIN jobs, climb / hoist / via another pole. */}
        <Route
          path="/climb-check"
          element={(
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <ClimbCheck />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          )}
        />
        {/* Operations: pole survey — Openreach climb status before dispatch. */}
        <Route
          path="/pole-survey"
          element={(
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <PoleMap />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          )}
        />

        {/* Email signature builder — any signed-in user. */}
        <Route
          path="/signatures"
          element={(
            <>
              <UnauthenticatedTemplate>{ssoAttempted ? <Login /> : null}</UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                <AuthenticatedLayout>
                  <EmailSignatures />
                </AuthenticatedLayout>
              </AuthenticatedTemplate>
            </>
          )}
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
